/**
 * Tree-checkbox selection maths for the PBS category picker — pure, so it can be unit-tested away
 * from React.
 *
 * The rules follow from how the reconcile reads an exclusion: it walks a product's category UP the
 * tree, so an excluded parent already covers every category beneath it. That gives three behaviours:
 *   - ticking a branch ticks its whole subtree (what you see is what gets excluded);
 *   - unticking anything also unticks its ancestors — otherwise a still-ticked parent would keep
 *     excluding the child you just cleared, and the screen would lie about what happens;
 *   - only the TOPMOST ticked category of a branch is worth storing; its ticked children are covered
 *     by it and would just be dead rows in the table.
 */

export interface TreeNode {
  categoryId: string;
  parentId: string | null;
}

/** parentId lookup for the whole tree. A parent that isn't in the set is treated as absent (a root). */
export function parentIndex(nodes: TreeNode[]): Map<string, string | null> {
  const known = new Set(nodes.map((n) => n.categoryId));
  return new Map(nodes.map((n) => [n.categoryId, n.parentId && known.has(n.parentId) ? n.parentId : null] as const));
}

/** categoryId → its direct children, derived from the same parent index. */
export function childIndex(nodes: TreeNode[]): Map<string, string[]> {
  const parentOf = parentIndex(nodes);
  const out = new Map<string, string[]>();
  for (const n of nodes) {
    const p = parentOf.get(n.categoryId) ?? null;
    if (p) out.set(p, [...(out.get(p) ?? []), n.categoryId]);
  }
  return out;
}

const MAX_DEPTH = 12; // deeper than any real PBS branch; also the cycle guard for a self-referencing parent

/** Walk from a node up to the root (excluding the node itself). Stops on a cycle. */
export function ancestorsOf(id: string, parentOf: Map<string, string | null>): string[] {
  const out: string[] = [];
  const seen = new Set([id]);
  let cur = parentOf.get(id) ?? null;
  for (let hops = 0; cur && hops < MAX_DEPTH; hops++) {
    if (seen.has(cur)) break;
    seen.add(cur);
    out.push(cur);
    cur = parentOf.get(cur) ?? null;
  }
  return out;
}

/** The node itself plus every category beneath it. */
export function subtreeIds(id: string, children: Map<string, string[]>): string[] {
  const out: string[] = [id];
  const stack = [...(children.get(id) ?? [])];
  const seen = new Set(out);
  while (stack.length) {
    const cur = stack.pop()!;
    if (seen.has(cur)) continue; // cycle guard
    seen.add(cur);
    out.push(cur);
    for (const kid of children.get(cur) ?? []) stack.push(kid);
  }
  return out;
}

/** Tick (subtree in) or untick (subtree + ancestors out) one category. Returns a NEW set. */
export function toggleSelection(sel: ReadonlySet<string>, id: string, children: Map<string, string[]>, parentOf: Map<string, string | null>): Set<string> {
  const next = new Set(sel);
  const subtree = subtreeIds(id, children);
  if (next.has(id)) {
    for (const d of subtree) next.delete(d);
    for (const a of ancestorsOf(id, parentOf)) next.delete(a);
  } else {
    for (const d of subtree) next.add(d);
  }
  return next;
}

/** Selected categories that have no selected ancestor — the only ones worth storing as a row. */
export function topmostSelected(sel: ReadonlySet<string>, parentOf: Map<string, string | null>): string[] {
  return [...sel].filter((id) => !ancestorsOf(id, parentOf).some((a) => sel.has(a)));
}

/** Branches with some (but not all) of their subtree selected → render an indeterminate checkbox. */
export function partiallySelected(sel: ReadonlySet<string>, parentOf: Map<string, string | null>): Set<string> {
  const out = new Set<string>();
  for (const id of sel) {
    for (const a of ancestorsOf(id, parentOf)) if (!sel.has(a)) out.add(a);
  }
  return out;
}
